<template>
    <div class="w-full">
        <div class="w-full flex justify-between">
            <el-input :placeholder="$t('general.search')" class="max-w-52" v-model="reportSearch">
                <i class="vc-icon-search icon-md text-primary el-input__icon"
                   slot="prefix"/>
            </el-input>

            <base-button type="primary" size="xs" link @click="addCreateTab">
                <i class="vc-icon-add icon-md mx-2"/>
                {{ $t('report.addNewReport') }}
            </base-button>
        </div>
        <div class="reports-table__wrapper mt-4">
            <el-table
                :data="paginatedData"
                :sortable="sortable"
                @sort-change="onSortChange"
                style="width: 100%"
                :class="$rtl.isRTL ? 'rtl' : 'ltr'"
            >

                <template slot="empty">
                    <div class="flex items-center justify-center flex-col">
                        <icon-no-data-cry class="w-64" />
                        <h3 class="text-main-xl">{{ $t('general.noData') }}</h3>
                    </div>
                </template>

                <el-table-column type="expand" min-width="50">
                    <template slot-scope="{row}">
                        <slot name="expand-content" :row="row"></slot>
                    </template>
                </el-table-column>

                <el-table-column
                    v-for="(column, index) in tableProps"
                    :key="`${column.label}-${index}`"
                    :label="column.label"
                    :prop="column.prop"
                    :min-width="column.minWidth"
                    :width="column.width"
                    sortable>
                    <template slot="header">
                        <div class="text-primary flex items-center">
                            <i v-if="column.icon" :class="column.icon" class="icon-md mx-1"/>
                            {{ column.label }}
                        </div>
                    </template>
                    <template slot-scope="{row}">
                        <slot :name="[column.prop]" :row="row">{{ row[column.prop] }}</slot>
                    </template>
                </el-table-column>

                <el-table-column
                    v-for="(column, index) in tableActionProps"
                    :width="column.width"
                    :key="`${column.label}-${index}`"
                >
                    <template slot="header">
                        <div class="text-primary flex items-center justify-center">
                            <i v-if="column.icon" :class="column.icon" class="icon-md mx-1"/>
                            {{ column.label }}
                        </div>
                    </template>
                    <template slot-scope="{row}">
                        <slot :name="[column.prop]" :row="row"/>
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <reports-pagination
            :currentPage="currentPage"
            :totalPages="totalPages"
            :paginationFrom="paginationFrom"
            :paginationTo="paginationTo"
            :paginationTotal="paginationTotal"
            @go-to-prev-page="goToPrevPage"
            @go-to-next-page="goToNextPage"
            @on-page-select="onPageSelect"/>
    </div>
</template>

<script>
import {Table, TableColumn, Tooltip} from 'element-ui'
import Fuse from 'fuse.js';

import BaseButton from "@/components/Common/Buttons/BaseButton";
import ButtonIcon from "@/modules/common/components/ButtonIcon";
import ReportsPagination from "@/modules/reports/components/ReportsPagination";
import cloneDeep from "lodash/cloneDeep";

export default {
    name: "reports-table",
    components: {
        BaseButton,
        [Table.name]: Table,
        [Tooltip.name]: Tooltip,
        [TableColumn.name]: TableColumn,
        ButtonIcon,
        ReportsPagination
    },
    props: {
        tableData: {
            type: Array,
            default: []
        },
        sortable: {
            type: [Boolean, String],
            default: false
        },
    },
    data() {
        return {
            reportSearch: '',
            searchData: [],
            currentPage: 1,
            perPage: 10,
            fuseSearch: null,
            defaultSort: {prop: 'ReportID', order: 'descending'}
        }
    },
    methods: {
        addCreateTab() {
            this.$emit('on-create-report')
        },
        onPageSelect(page) {
            this.currentPage = page
        },
        initFuseSearch() {

            const fuseProps = this.tableProps.map(column => {
                return column.prop === 'ReportItemList' ?
                    column.prop + '.WidgetTitle':
                    column.prop === 'ReportTriggerList' ?
                        column.prop + '.ReportTriggerName':
                        column.prop
            })

            this.fuseSearch = new Fuse(this.tableData, {
                keys: fuseProps,
                threshold: 0.3
            });
            if (this.defaultSort) {
                this.$nextTick(() => {
                    this.onSortChange(this.defaultSort)
                })
            }
        },
        onSortChange({column, prop, order}) {
            if (prop === null) {
                return
            }

            if (order === 'ascending') {
                this.searchData.sort((a, b) => (a[prop] > b[prop]) ? 1 : -1)
            } else if (order === 'descending') {
                this.searchData.sort((a, b) => (a[prop] < b[prop]) ? 1 : -1)
            }
        },
        goToPrevPage() {
            if (this.currentPage > 1) {
                this.currentPage--
            }
        },
        goToNextPage() {
            if (this.currentPage < this.totalPages.length) {
                this.currentPage++
            }
        },
        translateTemplateNameContentTag(widget) {
            return this.$te(widget.TemplateNameContentTag) ? this.$t(widget.TemplateNameContentTag) : widget.WidgetTitle
        }
    },
    computed: {
        paginatedData() {
            if (this.searchData.length < this.perPage) {
                return this.searchData
            }

            const f = this.perPage * this.currentPage
            return this.searchData.slice(f - this.perPage, f)
        },
        totalPages() {
            const length = this.searchData.length / this.perPage
            const fullLength = this.searchData.length % this.perPage?
                length  + 1:
                length

            let pages = []
            for (let i = 1; i <= fullLength; i++) {
                pages.push(i)
            }
            return pages
        },
        paginationFrom() {
            if (!this.searchData.length) return 0

            return this.currentPage * this.perPage - this.perPage + 1
        },
        paginationTo() {
            if (!this.searchData.length) return 0

            if (this.totalPages.length === this.currentPage) {
                const remainder = this.searchData.length % this.perPage
                if (remainder) {
                    return this.currentPage * this.perPage - this.perPage + remainder
                }
            }
            return this.currentPage * this.perPage
        },
        paginationTotal() {
            return this.searchData.length
        },
        isContentLoading() {
            return this.$store.state.dashboards.contentLoading
        },
        tableProps () {
            return [
                {
                    prop: 'ReportID',
                    label: this.$t('general.number.symbol'),
                    icon: '',
                    width: '50'
                },
                {
                    prop: 'ReportName',
                    label: this.$t('report.reportName'),
                    icon: 'vc-icon-name',
                    minWidth: '120'
                },
                {
                    prop: 'ReportItemList',
                    label: this.$t('widget.widgetName'),
                    icon: 'vc-icon-name',
                    minWidth: '250'
                },
                {
                    prop: 'ReportTriggerList'   ,
                    label: this.$t('report.schedule'),
                    icon: 'vc-icon-schedule-calendar',
                    minWidth: '250'
                },
                {
                    prop: 'ReportStatusID',
                    label: this.$t('general.status'),
                    icon: 'vc-icon-login-status',
                    width: '120'
                },
                {
                    prop: 'ReportExportTypes',
                    label: this.$t('report.format'),
                    icon: 'vc-icon-confirm-action',
                    width: '130'
                }
            ]
        },
        tableActionProps () {
            return [
                {
                    prop: 'action',
                    label: this.$t('general.action'),
                    icon: 'vc-icon-settings',
                    width: '100'
                }
            ]
        }
    },
    watch: {
        tableData: {
            handler(newV) {
                this.initFuseSearch()
                //this.reportSearch = ''
                // TODO: reset table data
                this.searchData = cloneDeep(newV)

            }
        },
        reportSearch(newV) {
            let result

            this.currentPage = 1
            if (newV !== '') {
                result = this.fuseSearch.search(newV).map(el => el.item);
            } else {
                result = this.tableData
            }

            this.searchData = result;
        }
    },
}
</script>

<style scoped lang="scss">
.reports-table__wrapper ::v-deep .el-table {
    border: 1px solid var(--gray-300);
    @apply rounded-t-1xl border-b-0;

    .el-table__expand-column {
        width: 20px;
        .el-table__expand-icon {
            @apply text-primary;
            width: 30px;
            height: 20px;
            z-index: 5;
        }
    }
}
[dir="ltr"] .reports-table__wrapper ::v-deep .el-table {
    .el-table__cell:not(:last-child):not(.el-table__expand-column) {
        border-right: 1px solid var(--gray-300);
    }
    tr td.el-table__cell:nth-child(2) .cell {
        margin-left: -45px;
    }
}
[dir="rtl"] .reports-table__wrapper ::v-deep .el-table {
    .el-table__cell:not(:first-child):not(.el-table__expand-column) {
        border-left: 1px solid var(--gray-300);
    }

    .el-table__expand-icon>.el-icon {
        left: 0;
        right: 50% !important;
    }
    tr td.el-table__cell:nth-child(2) .cell {
        margin-right: -45px;
    }
}

.el-table ::v-deep th:hover.el-table__cell>.cell {
    @apply flex;
}

.el-table ::v-deep .el-table__expanded-cell {
    @apply p-0;
}
::v-deep .el-table th.el-table__cell>.cell {
    display: flex;
    justify-content: center;
}

::v-deep .el-table.ltr th.el-table__cell:nth-child(2) > .cell {
    margin-left: -25px !important;
}
::v-deep .el-table.rtl th.el-table__cell:nth-child(2) > .cell {
    margin-right: -22px !important;
    width: 55px;
}
::v-deep .el-table.rtl th.el-table__cell:nth-child(2) > .cell .caret-wrapper {
    @apply pr-5;
}
::v-deep .el-table th.el-table__cell:nth-child(2), ::v-deep .el-table th.el-table__cell:nth-child(1) {
    overflow: visible !important
}
::v-deep .el-table__expand-icon>.el-icon {
    margin-top: -7px !important;
}
</style>
